/**
 * ProductPickerModal — aranabilir ürün seçici modal.
 *
 * Ürünü UUID elle yazmak veya tüm listeyi bir dropdown'a basmak yerine bu
 * modaldan seçtirir. Arama SERVER-SIDE'dır (debounced → /products/?search=...):
 * binlerce ürün olsa bile DOM'a yalnız eşleşen sayfa kadar satır basılır ve
 * infinite-scroll ile sonraki sayfalar çekilir — düz bir Select gibi tüm
 * listeyi yüklemez (büyük menülerde dropdown patlamasını önler).
 *
 * `excludeIds` ile zaten ilişkilendirilmiş (örn. reçetesi olan) ürünler
 * listeden gizlenir.
 */
import { useState } from "react";
import {
  Badge, Box, Group, Image, Loader, Modal, ScrollArea, Stack, Text, TextInput,
  UnstyledButton, Button,
} from "@mantine/core";
import { IconPhoto, IconSearch } from "@tabler/icons-react";
import { useDebouncedValue } from "@mantine/hooks";
import { useInfiniteResource } from "../../../shared/api";
import { MoneyDisplay, useInfiniteScrollRef } from "../../../shared/ui";

interface Product {
  id: string;
  name: string;
  sku: string;
  unit_price_minor: number;
  image_thumbnail?: string | null;
  image?: string | null;
  is_active: boolean;
}

interface Props {
  opened: boolean;
  onClose: () => void;
  /** Seçilen ürün — modal kapanır. */
  onSelect: (product: Product) => void;
  /** Listeden gizlenecek ürün id'leri (örn. zaten reçetesi olanlar). */
  excludeIds?: string[];
}

export function ProductPickerModal({ opened, onClose, onSelect, excludeIds = [] }: Props) {
  const [search, setSearch] = useState("");
  const [debounced] = useDebouncedValue(search.trim(), 300);

  const {
    items, isLoading, hasNextPage, isFetchingNextPage, fetchNextPage,
  } = useInfiniteResource<Product>("products", { search: debounced });

  const excluded = new Set(excludeIds);
  const visible = items.filter((p) => !excluded.has(p.id));

  const sentinelRef = useInfiniteScrollRef<HTMLDivElement>({
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    itemCount: items.length,
  });

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title="Ürün Seç"
      size="md"
    >
      <Stack gap="sm">
        <Text size="xs" c="dimmed">İlişkilendirmek için ürün arayın ve seçin</Text>
        <TextInput
          placeholder="Ürün adı veya SKU ile ara..."
          leftSection={<IconSearch size={16} />}
          value={search}
          onChange={(e) => setSearch(e.currentTarget.value)}
          autoFocus
        />

        {/* offsetScrollbars: scrollbar'a yer ayır — kart içeriğinin üstüne binmesin. */}
        <ScrollArea.Autosize mah={420} offsetScrollbars>
          {isLoading ? (
            <Group justify="center" py="xl"><Loader /></Group>
          ) : visible.length === 0 ? (
            <Text c="dimmed" ta="center" py="xl" size="sm">
              {debounced ? "Eşleşen ürün yok" : "Henüz ürün yok"}
            </Text>
          ) : (
            <Stack gap={8}>
              {visible.map((p) => (
                <UnstyledButton
                  key={p.id}
                  onClick={() => { onSelect(p); onClose(); }}
                  style={{
                    border: "1px solid var(--mantine-color-gray-3)",
                    borderRadius: "var(--mantine-radius-md)",
                    padding: 10,
                  }}
                >
                  <Group gap="sm" wrap="nowrap">
                    {(p.image_thumbnail || p.image) ? (
                      <Image
                        src={p.image_thumbnail || p.image || undefined}
                        w={48}
                        h={48}
                        radius="sm"
                        fit="cover"
                        alt={p.name}
                      />
                    ) : (
                      <Group justify="center" w={48} h={48} bg="gray.1" style={{ borderRadius: 6, flexShrink: 0 }}>
                        <IconPhoto size={20} color="var(--mantine-color-gray-5)" />
                      </Group>
                    )}
                    <Box style={{ flex: 1, minWidth: 0 }}>
                      <Text fw={600} size="sm" truncate>{p.name}</Text>
                      <Text size="xs" c="dimmed" truncate>SKU: {p.sku || "-"}</Text>
                      <Text size="sm" fw={500} c="teal">
                        <MoneyDisplay minor={p.unit_price_minor} />
                      </Text>
                    </Box>
                    <Badge
                      color={p.is_active ? "green" : "gray"}
                      variant="light"
                      style={{ flexShrink: 0 }}
                    >
                      {p.is_active ? "aktif" : "pasif"}
                    </Badge>
                  </Group>
                </UnstyledButton>
              ))}
              {/* Infinite-scroll sentinel — görününce sonraki sayfa çekilir */}
              {hasNextPage && (
                <Box ref={sentinelRef}>
                  <Group justify="center" py="sm"><Loader size="xs" /></Group>
                </Box>
              )}
            </Stack>
          )}
        </ScrollArea.Autosize>

        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>İptal</Button>
        </Group>
      </Stack>
    </Modal>
  );
}
